import { useState, useCallback } from 'react'

type OtpStatus = 'idle' | 'sending' | 'sent' | 'verifying' | 'verified' | 'error'

interface ExistingApp {
  status: string
  redirect_to: string
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

// Parses the response as JSON when possible. If the server returned an HTML
// error page (e.g. a 500 / CSRF page), surfaces the HTTP status instead of
// silently catching the JSON parse error and showing "Network error".
async function parseOrThrow(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text()
  try {
    return JSON.parse(text) as Record<string, unknown>
  } catch {
    throw new Error(`Server returned ${res.status} ${res.statusText}. Please refresh and try again.`)
  }
}

export function useOtp() {
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [existingApp, setExistingApp] = useState<ExistingApp | null>(null)

  const sendOtp = useCallback(async (email: string) => {
    setOtpStatus('sending')
    setOtpError(null)

    try {
      const res = await fetch('/applications/check_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ email })
      })

      const data = await parseOrThrow(res)

      if (!res.ok) {
        setOtpStatus('error')
        setOtpError((data.error as string) || `Failed to send verification code (${res.status})`)
        return
      }

      setOtpStatus('sent')
    } catch (err) {
      console.error('[useOtp] sendOtp failed:', err)
      setOtpStatus('error')
      setOtpError(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }, [])

  const verifyOtp = useCallback(async (email: string, otp: string) => {
    setOtpStatus('verifying')
    setOtpError(null)

    try {
      const res = await fetch('/applications/verify_email_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ email, otp })
      })

      const data = await parseOrThrow(res)

      if (!res.ok) {
        setOtpStatus('error')
        setOtpError((data.error as string) || `Verification failed (${res.status})`)
        return
      }

      if (data.existing_application) {
        setExistingApp({
          status: data.status as string,
          redirect_to: data.redirect_to as string
        })
      }

      setOtpStatus('verified')
    } catch (err) {
      console.error('[useOtp] verifyOtp failed:', err)
      setOtpStatus('error')
      setOtpError(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }, [])

  const resetOtp = useCallback(() => {
    setOtpStatus('idle')
    setOtpError(null)
    setExistingApp(null)
  }, [])

  return { otpStatus, otpError, existingApp, sendOtp, verifyOtp, resetOtp }
}
